/**
 * The robo-cat's "alive" behaviour as a pure state machine, driven by timestamps so it is
 * testable without timers. The UI hook dispatches events and schedules a single timer for
 * `nextDeadline()`.
 *
 *   awake ──idle 25 s──▶ yawning ──1.4 s──▶ asleep ──tap──▶ waking ──0.7 s──▶ awake
 *     │  ▲                  │ activity/tap → awake (tap startles)
 *   tap│  │0.32 s
 *     ▼  │
 *  startled          5 taps in 2.5 s ──▶ angry ──4 s after the last tap──▶ awake
 */

export type CatPhase = 'awake' | 'startled' | 'yawning' | 'asleep' | 'waking' | 'angry';

export interface CatMood {
  phase: CatPhase;
  /** When the current timed phase ends (startled, yawning, waking, angry); 0 when untimed. */
  until: number;
  /** Last interaction anywhere in the app; drives falling asleep. */
  lastActivity: number;
  /** Recent taps on the cat itself, newest last, within ANGRY_WINDOW_MS. */
  taps: readonly number[];
}

export type CatEvent =
  | { type: 'tap'; at: number }
  | { type: 'activity'; at: number }
  | { type: 'tick'; at: number };

export const IDLE_MS = 25_000;
export const YAWN_MS = 1_400;
export const STARTLE_MS = 320;
export const WAKE_MS = 700;
export const ANGRY_TAPS = 5;
export const ANGRY_WINDOW_MS = 2_500;
export const ANGRY_MS = 4_000;

export function initialMood(at: number): CatMood {
  return { phase: 'awake', until: 0, lastActivity: at, taps: [] };
}

function awake(at: number, taps: readonly number[] = []): CatMood {
  return { phase: 'awake', until: 0, lastActivity: at, taps };
}

function onTap(mood: CatMood, at: number): CatMood {
  const taps = [...mood.taps.filter((t) => at - t < ANGRY_WINDOW_MS), at];
  if (mood.phase === 'asleep') {
    return { phase: 'waking', until: at + WAKE_MS, lastActivity: at, taps: [at] };
  }
  if (mood.phase === 'angry') {
    // Poking an angry cat keeps it angry a little longer.
    return { ...mood, until: at + ANGRY_MS, lastActivity: at, taps };
  }
  if (taps.length >= ANGRY_TAPS) {
    return { phase: 'angry', until: at + ANGRY_MS, lastActivity: at, taps: [] };
  }
  return { phase: 'startled', until: at + STARTLE_MS, lastActivity: at, taps };
}

function onActivity(mood: CatMood, at: number): CatMood {
  // Only a tap on the cat wakes it; other activity just keeps an awake cat from dozing off.
  if (mood.phase === 'asleep') return mood;
  if (mood.phase === 'yawning') return awake(at, mood.taps);
  return mood.lastActivity === at ? mood : { ...mood, lastActivity: at };
}

function onTick(mood: CatMood, at: number): CatMood {
  switch (mood.phase) {
    case 'startled':
    case 'waking':
    case 'angry':
      return at >= mood.until ? awake(at, mood.phase === 'startled' ? mood.taps : []) : mood;
    case 'yawning':
      return at >= mood.until ? { ...mood, phase: 'asleep', until: 0 } : mood;
    case 'awake':
      return at - mood.lastActivity >= IDLE_MS
        ? { ...mood, phase: 'yawning', until: at + YAWN_MS }
        : mood;
    case 'asleep':
      return mood;
  }
}

export function catMoodReducer(mood: CatMood, event: CatEvent): CatMood {
  switch (event.type) {
    case 'tap':
      return onTick(onTap(mood, event.at), event.at);
    case 'activity':
      return onActivity(mood, event.at);
    case 'tick':
      return onTick(mood, event.at);
  }
}

/** When the next `tick` must fire for the state to advance on its own; null while asleep. */
export function nextDeadline(mood: CatMood): number | null {
  switch (mood.phase) {
    case 'awake':
      return mood.lastActivity + IDLE_MS;
    case 'asleep':
      return null;
    default:
      return mood.until;
  }
}
